import capitalize from 'lodash/capitalize';

import { GProduct, IPrismicSlice } from '@/types';
import { buildSearchBodyFromParams, SearchService } from '@/opensearch';
import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';

import {
  getSelectedCustomerTypeFromServer,
  getSelectedFulfillmentTypeFromServer,
  getSelectedStoreFromServer,
} from './server';
import { getProductsFromOpenSearchResponse } from './opensearch';

let slice: IPrismicSlice;

type SliceType = typeof slice.slice_type;

const SORT_BY_MAP = {
  default: undefined,
  'Price (desc)': { value: { field: 'price', order: 'desc' } },
  'Price (asc)': { value: { field: 'price', order: 'asc' } },
  'Expiration Date (Soonest First)': {
    value: { field: 'customExpirationDateOld', order: 'asc' },
  },
  'Expiration Date (Latest First)': {
    value: { field: 'customExpirationDateNew', order: 'desc' },
  },
  'Product Creation Date (Oldest to Newest)': {
    value: { field: 'createdDate', order: 'asc' },
  },
  'Product Creation Date (Newest to Oldest)': {
    value: { field: 'createdDate', order: 'desc' },
  },
};

type SortByMapKey = keyof typeof SORT_BY_MAP;

export type SortQueryParam = { sort: string };

function sortParamFromPrismicSortBy(sort_by?: SortByMapKey): SortQueryParam {
  const mapped =
    typeof sort_by === 'string' && sort_by in SORT_BY_MAP
      ? SORT_BY_MAP[sort_by]
      : undefined;

  if (!mapped) {
    return { sort: '' };
  }

  const { field, order } = mapped.value;

  return { sort: `${field}${capitalize(order)}` };
}

export type SliceItems = {
  [key in SliceType]?: Extract<IPrismicSlice, { slice_type: key }>;
};

const getPrismicSlicesItems = (body: IPrismicSlice[]) => {
  let slices: SliceItems = {};

  Object.keys(body).forEach((key: string) => {
    const slice: IPrismicSlice = body[key];

    const value: SliceItems = { [slice.slice_type]: slice };

    slices = { ...slices, ...value };
  });

  return slices;
};

const getPrismicSlicesList = (slices: IPrismicSlice[]) =>
  Promise.all(
    slices.map(async (slice: IPrismicSlice) => {
      if (!slice.items && !slice.primary) return;

      const { selectedStore } = await getSelectedStoreFromServer();
      const { selectedCustomerType } =
        await getSelectedCustomerTypeFromServer();
      const { selectedFulfillmentType } =
        await getSelectedFulfillmentTypeFromServer();

      const { search } = new SearchService({
        entityId: selectedStore?.mso_store_entity_id,
        orgId: selectedStore?.mso_org_id,
        customerType: selectedCustomerType,
        isStrictMedicalEnabled: selectedStore?.strict_medical_enabled,
        fulfillmentType: selectedFulfillmentType,
      });

      if (
        slice.slice_type === 'products_carousel' ||
        slice.slice_type === 'kiosk_products_carousel' ||
        slice.slice_type === 'product_side_by_side'
      ) {
        try {
          const {
            limit = 20,
            type,
            sort_by,
            filter_by_brand,
            product_group_id,
            filter_by_on_sale,
          } = slice.primary;

          const sortParam = sortParamFromPrismicSortBy(sort_by);

          const groupIdParam = {
            [PRODUCT_FILTER_ATTRIBUTES._GROUP_ID]:
              product_group_id?.toString() ?? [],
          };

          const onSaleParam = {
            [PRODUCT_FILTER_ATTRIBUTES._ON_SALE]: filter_by_on_sale
              ? ['true']
              : [],
          };

          const categoryParam = {
            [PRODUCT_FILTER_ATTRIBUTES._CATEGORY]:
              type && type !== 'default' ? [type.toUpperCase()] : [],
          };

          const brandParam = {
            [PRODUCT_FILTER_ATTRIBUTES._BRAND]:
              filter_by_brand && filter_by_brand !== 'default'
                ? [filter_by_brand?.replace(' ', '_').toUpperCase()!]
                : [],
          };

          let flavorsParam: Record<string, string[]> = {};
          let effectsParam: Record<string, string[]> = {};
          let generalsParam: Record<string, string[]> = {};

          if (
            slice.slice_type === 'products_carousel' ||
            slice.slice_type === 'kiosk_products_carousel'
          ) {
            const { flavors, effects, generals } = slice.primary;

            flavorsParam = {
              [PRODUCT_FILTER_ATTRIBUTES._FLAVORS]: flavors
                ? flavors.split(',').map(flavor => flavor.trim().toUpperCase())
                : [],
            };
            effectsParam = {
              [PRODUCT_FILTER_ATTRIBUTES._EFFECTS]: effects
                ? effects.split(',').map(effect => effect.trim().toUpperCase())
                : [],
            };
            generalsParam = {
              [PRODUCT_FILTER_ATTRIBUTES._GENERALS]: generals
                ? generals
                    .split(',')
                    .map(general => general.trim().toUpperCase())
                : [],
            };
          }

          const attributesParams = {
            ...categoryParam,
            ...brandParam,
            ...onSaleParam,
            ...groupIdParam,
            ...flavorsParam,
            ...effectsParam,
            ...generalsParam,
          };

          const hasAttributesValues = Object.values(attributesParams).some(
            param => !!param.length,
          );

          let products: GProduct[] = [];

          if (hasAttributesValues) {
            const { searchBody } = buildSearchBodyFromParams({
              searchParams: {
                ...sortParam,
                ...attributesParams,
              },
              filters: {
                isAboveThreshold: [true],
                isHideFromMenu: [false],
              },
              limit: limit ? parseInt(limit?.toString(), 10) : 20,
              omitAggs: true,
            });

            const result = await search({ searchBody });

            products = getProductsFromOpenSearchResponse({
              result,
              isSortingApplied: !!sortParam.sort,
            });
          }

          return {
            ...slice,
            products,
            totalCount: 0,
            selectedStore,
            selectedCustomerType,
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);

          return { ...slice, products: [], totalCount: 0 };
        }
      }

      if (['product_list', 'product_row'].includes(slice.slice_type)) {
        const ids = slice?.items
          ?.map(item => item.product_id?.trim())
          .filter((id): id is string => !!id);

        if (!ids?.length) {
          return { ...slice, products: [], selectedStore };
        }

        const idParam: { id: string[] } = {
          id: ids ?? [],
        };

        const sortParam = sortParamFromPrismicSortBy(
          slice.slice_type === 'product_list'
            ? slice.primary.sort_by
            : undefined,
        );

        const { searchBody } = buildSearchBodyFromParams({
          searchParams: {
            ...sortParam,
            ...idParam,
          },
          filters: {
            isAboveThreshold: [true],
            isHideFromMenu: [false],
          },
          omitAggs: true,
        });

        const result = await search({ searchBody });

        const products = getProductsFromOpenSearchResponse({
          result,
          isSortingApplied: !!sortParam.sort,
        });

        return { ...slice, products, selectedStore };
      }

      return slice;
    }),
  );

const mergeSlices = (currentSlices, defaultSlices) => {
  // Validate that currentSlices is an array
  const validCurrentSlices = Array.isArray(currentSlices) ? currentSlices : [];
  const validDefaultSlices = Array.isArray(defaultSlices) ? defaultSlices : [];

  const currentSliceTypes = new Set(
    validCurrentSlices.map(slice => slice?.slice_type),
  );

  const missingDefaultSlices = validDefaultSlices.filter(
    slice => !currentSliceTypes.has(slice?.slice_type),
  );

  // Ensure all default slices are in the map (preserve current document slices)
  return [...validCurrentSlices, ...missingDefaultSlices];
};

const mergeDocumentSlices = (
  document: {
    data: { slices?: IPrismicSlice[]; bottomSlices?: IPrismicSlice[] };
  },
  defaultDocument: {
    data: { slices?: IPrismicSlice[]; bottomSlices?: IPrismicSlice[] };
  },
) => {
  return {
    slices: mergeSlices(document?.data?.slices, defaultDocument?.data?.slices),
    bottomSlices: mergeSlices(
      document?.data?.bottomSlices,
      defaultDocument?.data?.bottomSlices,
    ),
  };
};

export {
  getPrismicSlicesItems,
  getPrismicSlicesList,
  mergeDocumentSlices,
  mergeSlices,
  sortParamFromPrismicSortBy,
  SORT_BY_MAP,
};
